import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { Box, FormControl, Select, MenuItem } from '@mui/material';
import LoadingDots from '../LoadingDots/LoadingDots'; // <-- dots with header variant
import { toTitleCase } from '../../utils/casing';

const CustomDropdownIcon = () => (
  <span
    className="material-symbols-rounded"
    style={{
      fontWeight: 500,
      fontSize: '1.5rem',
      color: '#757575',
      display: 'inline-block',
      position: 'absolute',
      right: '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      pointerEvents: 'none',
    }}
  >
    arrow_drop_down
  </span>
);

const LOADING_PHRASES_TAIL = [
  'Getting your deals back',
  'Finding the best',
  'Counting up deals',
];

const MobileResultsHeader = ({
  initialLoadDone,
  sortOption,
  setSortOption,
  headerText,
  loadingText = 'Talking to insurers',
  sortOptions,
}) => {
  const loadingPhrases = [loadingText, ...LOADING_PHRASES_TAIL];
  // phrase cycling
  const [phraseIndex, setPhraseIndex] = useState(0);
  const headingRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (headingRef.current) gsap.set(headingRef.current, { opacity: 1 });

    if (initialLoadDone) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const runFadeCycle = () => {
      if (!headingRef.current) return;
      const tl = gsap.timeline();
      tl.to(headingRef.current, {
        opacity: 0,
        duration: 0,
        ease: 'power2.out',
      })
        .add(() => {
          setPhraseIndex((i) => (i + 1) % loadingPhrases.length);
        })
        .to(headingRef.current, {
          opacity: 1,
          duration: 0.25,
          ease: 'power2.in',
        });
    };

    intervalRef.current = setInterval(runFadeCycle, 5000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [initialLoadDone]);

  const HippoAvatar =
    'https://assets.askhippo.co.za/avatars/v2/Point-and-grunt-v2.gif';

  return (
    <Box
      sx={{
        maxWidth: '100%',
        margin: '0 16px 16px 16px',
        pt: '0.5rem',
        pb: '0.5rem',
        boxSizing: 'border-box',
      }}
    >
      {/* Avatar + Heading */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          component="img"
          src={HippoAvatar}
          alt="HippoAvatar"
          sx={{ width: '80px', height: '80px', mr: 1.5, objectFit: 'contain' }}
        />

        {/* Heading (HTML when loaded; LoadingDots while loading) */}
        <Box
          ref={headingRef}
          sx={{
            fontFamily: 'Inter',
            fontWeight: initialLoadDone ? 400 : 700,
            fontSize: '1.5rem', // 2.125rem on desktop
            lineHeight: '120%',
            color: '#1E354E',
            // ↓ NO display:flex here
            whiteSpace: 'normal', // allow wrapping
          }}
        >
          {initialLoadDone ? (
            <span dangerouslySetInnerHTML={{ __html: headerText }} />
          ) : (
            // Make both text and dots part of the SAME inline formatting context
            <span>
              {loadingPhrases[phraseIndex]}
              <LoadingDots variant="header" />
            </span>
          )}
        </Box>
      </Box>

      {/* Sort Dropdown */}
      {initialLoadDone && sortOptions?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <FormControl
            variant="outlined"
            fullWidth
            sx={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #D1D1D1',
              borderRadius: '1rem',
              '& .MuiInputBase-root': {
                height: '3rem',
                pl: 0,
                alignItems: 'center',
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '1rem',
                color: '#757575',
                borderRadius: '1rem',
              },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            }}
          >
            <Select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              sx={{
                width: '100%',
                '.MuiSelect-select': {
                  fontFamily: 'Inter, sans-serif',
                  lineHeight: '1.2rem',
                  letterSpacing: '-0.02em',
                  color: '#757575',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  transition: 'none',
                  textTransform: 'none',
                  ml: '12px',
                },
                '.MuiSelect-icon': {
                  fontSize: '1.375rem',
                  color: '#757575',
                  marginRight: '1rem',
                  pointerEvents: 'none',
                },
              }}
              IconComponent={CustomDropdownIcon}
              displayEmpty
            >
              {sortOptions &&
                sortOptions.length > 0 &&
                sortOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {toTitleCase(option.label)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
      )}
    </Box>
  );
};

export default MobileResultsHeader;
